import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleChartsModule } from 'angular-google-charts';
import { HttpClient } from '@angular/common/http';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import {
  PERIODOS_ORDEN,
  RegistroPuntoNota,
} from '../_mock/historico-record.mock';

interface RegistroPuntoNotaExtendido extends RegistroPuntoNota {
  estudiante: string;
}

interface GraficaVm {
  title: string;
  type: 'ScatterChart';
  data: (number | null)[][];
  options: any;
  columns: { type: string; label: string }[];
}

@Component({
  selector: 'app-historico-record',
  standalone: true,
  imports: [
    CommonModule,
    GoogleChartsModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './historico-record.component.html',
  styleUrls: ['./historico-record.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class HistoricoRecordComponent implements OnInit {

  vm: { graficas: GraficaVm[] } | null = null;
  loading = true;
  error: string | null = null;

  // datos base
  private registrosTotales: RegistroPuntoNotaExtendido[] = [];

  // filtro de estudiantes
  estudiantes: string[] = [];
  estudianteSeleccionado: string | null = null;

  // orden dinámico (Y) de materias
  materiasOrdenadas: string[] = [];

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    const url = 'assets/historico/notas_25.csv';

    this.http.get(url, { responseType: 'text' }).subscribe({
      next: (csvText) => {
        const registros = this.parseCsvToRegistros(csvText);
        this.registrosTotales = registros;

        // lista de estudiantes únicos (alfabético)
        this.estudiantes = Array.from(
          new Set(registros.map(r => r.estudiante))
        ).sort((a, b) => a.localeCompare(b));

        // seleccionar uno por defecto (Santiago si existe)
        const porDefecto = this.estudiantes.find(e => e === 'Santiago Moreno Ruiz');
        this.estudianteSeleccionado = porDefecto ?? this.estudiantes[0] ?? null;

        this.actualizarGrafica();
        this.loading = false;
      },
      error: () => {
        this.error = 'No se pudo cargar el archivo de notas.';
        this.loading = false;
      },
    });
  }

  // cambio desde el mat-select
  onChangeEstudiante(est: string) {
    this.estudianteSeleccionado = est;
    this.actualizarGrafica();
  }

  // reconstruir vm según estudianteSeleccionado
  private actualizarGrafica(): void {
    if (!this.estudianteSeleccionado) {
      this.vm = { graficas: [] };
      return;
    }

    const registrosEst = this.registrosTotales.filter(
      r => r.estudiante === this.estudianteSeleccionado
    );

    // materias ordenadas alfabéticamente (Z → A, como lo tenías)
    this.materiasOrdenadas = Array.from(
      new Set(registrosEst.map(r => r.materia))
    ).sort((a, b) => b.localeCompare(a));

    this.vm = {
      graficas: [this.buildGraficaScatter(registrosEst)],
    };
  }

  // CSV → objetos
  private parseCsvToRegistros(csv: string): RegistroPuntoNotaExtendido[] {
    const lines = csv.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length <= 1) return [];

    const headerLine = lines[0];
    const semiCount = (headerLine.match(/;/g) || []).length;
    const commaCount = (headerLine.match(/,/g) || []).length;
    const delimiter = semiCount > commaCount ? ';' : ',';

    const header = headerLine.split(delimiter).map(h => h.trim());

    const idxMateria = header.indexOf('Materia');
    const idxPeriodo = header.indexOf('periodos_academico');
    const idxNota = header.indexOf('Nota_cualitativa');
    const idxEstudiante = header.indexOf('Estudiante');

    if (idxMateria === -1 || idxPeriodo === -1 || idxNota === -1 || idxEstudiante === -1) {
      console.warn('[HistoricoRecord] Faltan columnas esperadas en el CSV');
      return [];
    }

    const registros: RegistroPuntoNotaExtendido[] = [];

    for (let i = 1; i < lines.length; i++) {
      const row = lines[i].split(delimiter);

      if (row.length <= Math.max(idxMateria, idxPeriodo, idxNota, idxEstudiante)) continue;

      const materia = row[idxMateria]?.trim();
      const periodo = row[idxPeriodo]?.trim();
      const notaRaw = row[idxNota]?.trim();
      const estudiante = row[idxEstudiante]?.trim();

      if (!materia || !periodo || !estudiante) continue;

      registros.push({
        materia,
        periodoAcademico: periodo,
        notaLabel: this.mapNotaCualitativa(notaRaw),
        estudiante,
      });
    }

    return registros;
  }

  // mapeo Nota_cualitativa → label
  private mapNotaCualitativa(val: string): RegistroPuntoNota['notaLabel'] {
    const v = (val ?? '').trim();

    if (v === '4') return 'Superior';
    if (v === '3') return 'Satisfactorio';
    if (v === '2') return 'En Desarrollo';
    if (v === '1') return 'Insuficiente';
    if (v === '-1') return 'Aprobó';
    if (v === '-2') return 'No Aprobó';

    return 'Sin dato';
  }

  // construir gráfica para un estudiante
  private buildGraficaScatter(registros: RegistroPuntoNota[]): GraficaVm {
    const niveles = [
      'Superior',
      'Satisfactorio',
      'En Desarrollo',
      'Insuficiente',
      'Aprobó',
      'No Aprobó',
      'Sin dato',
    ] as const;

    const indexNivel = new Map<string, number>();
    niveles.forEach((n, i) => indexNivel.set(n, i));

    const columns: { type: string; label: string }[] = [
      { type: 'number', label: 'PeriodoIndex' },
      { type: 'number', label: 'Superior' },
      { type: 'number', label: 'Satisfactorio' },
      { type: 'number', label: 'En Desarrollo' },
      { type: 'number', label: 'Insuficiente' },
      { type: 'number', label: 'Aprobó' },
      { type: 'number', label: 'No Aprobó' },
      { type: 'number', label: 'Sin dato' },
    ];

    const data: (number | null)[][] = [];

    for (const r of registros) {
      const xIndex = PERIODOS_ORDEN.indexOf(r.periodoAcademico);
      const yIndex = this.materiasOrdenadas.indexOf(r.materia);
      const serieIdx = indexNivel.get(r.notaLabel) ?? -1;

      if (xIndex === -1 || yIndex === -1 || serieIdx === -1) continue;

      const fila = new Array(columns.length).fill(null) as (number | null)[];
      fila[0] = xIndex;
      fila[1 + serieIdx] = yIndex;

      data.push(fila);
    }

    return {
      title: `Histórico académico - ${this.estudianteSeleccionado}`,
      type: 'ScatterChart',
      data,
      columns,
      options: {
        // ⭐ 1. TÍTULO CENTRADO Y CON MARGEN ⭐
        title: `Histórico académico - ${this.estudianteSeleccionado}`,
        titleTextStyle: {
          fontSize: 20,
          bold: true,
          color: '#333',
        },
        // margen superior extra
        chartArea: {
          left: 150,
          right: 40,
          top: 90,      // ← Aumentado (antes era 40)
          bottom: 60,
        },

        // ⭐ 2. LEYENDA MÁS SEPARADA ⭐
        legend: {
          position: 'top',
          alignment: 'center',
          textStyle: {
            fontSize: 14,
          },
          maxLines: 3,        // permite mejor distribución
        },

        // ⭐ 3. PUNTOS MÁS GRANDES ⭐
        pointSize: 10,   // antes era 7

        hAxis: {
          title: null,
          ticks: PERIODOS_ORDEN.map((p, i) => ({ v: i, f: p })),
          textStyle: { fontSize: 11 },

          // ⭐ NO mueve los puntos (se quedan en mismo xIndex)
          viewWindow: {
            min: -0.5,
            max: PERIODOS_ORDEN.length - 0.5,
          },

          // ⭐ HACEMOS LA LÍNEA MUY DELGADA
          baselineColor: 'transparent',   // color más suave
          baseline: 0,             // mantiene la línea en el primer periodo
          gridlines: {
            color: '#ddd',
            count: PERIODOS_ORDEN.length + 1,
            // línea MUY delgada
            minSpacing: 20,
          },
          minorGridlines: {
            color: '#eee',
          }
        },

        vAxis: {
          title: null,
          ticks: this.materiasOrdenadas.map((m, i) => ({ v: i, f: m })),
          gridlines: { count: this.materiasOrdenadas.length },
          textStyle: { fontSize: 11 },
        },

        series: {
          0: { color: '#4cb748' }, // Superior
          1: { color: '#24c1df' }, // Satisfactorio
          2: { color: '#fede09' }, // En Desarrollo
          3: { color: '#61368b' }, // Insuficiente
          4: { color: '#98cf8b' }, // Aprobó
          5: { color: '#9278af' }, // No Aprobó
          6: { color: '#9e9e9e' }, // Sin dato
        },
      }

    };
  }
}
